import React, { useEffect, useState } from 'react';
import SectionBasePage from 'components/SectionBasePage';
import { useDispatch, useSelector } from 'hooks/store';
import {
  cleanHealingRoomsAction,
  createHealingRoomAction,
  healingRoomsEntitiesSelector,
  healingRoomsIdsSelector,
  getHealingRoomListAction,
  isLoadingHealingRoomsSelector,
  remoteTotalSelector,
  updateHealingRoomAction,
} from 'state/slices/healingRoom';
import { usePagination } from 'hooks/pagination';
import { HealingRoom, PaginationData } from 'constants/interface';
import { Table } from 'UI';
import { Form } from 'antd';
import { createAlertNotification } from 'infra/notification';
import { AlertNotificationType, setNotificationAction } from 'state/slices/notification';
import { CreateHealingRoomData } from 'api/healingRoom';
import { unwrapResult } from '@reduxjs/toolkit';

import EditModal, { HealingRoomFormData } from './components/EditModal';
import Extra from './components/Extra';

import {
  ActionButton,
} from './style';

const HealingPage = (): JSX.Element => {
  const dispatch = useDispatch();
  const remoteTotal = useSelector(remoteTotalSelector);

  const roomEntities = useSelector(healingRoomsEntitiesSelector);
  const roomIds = useSelector(healingRoomsIdsSelector);
  const isRemoteLoading = useSelector(isLoadingHealingRoomsSelector);
  const [isLoading, setLoading] = useState(false);

  const rooms = roomIds.map((roomId) => {
    const entity = roomEntities[roomId];
    if (!entity) return undefined;
    const tableRoom: HealingRoom & {
      key?: string,
    } = {
      ...entity,
      key: roomId.toString(),
    };
    return tableRoom;
  }).filter((tableRoom) => !!tableRoom) as (HealingRoom & { key: string})[];

  const remoteFetch = (
    paginationData: PaginationData,
  ) => {
    dispatch(getHealingRoomListAction(paginationData));
  };
  const pagination = usePagination<object>( // eslint-disable-line @typescript-eslint/ban-types
    remoteFetch,
    {
      filter: {
        isActive: true,
      },
    },
  );

  useEffect(() => {
    dispatch(cleanHealingRoomsAction());
  }, []);

  useEffect(() => {
    pagination.updateTotal(remoteTotal);
  }, [remoteTotal]);

  const [healingRoomForm] = Form.useForm<HealingRoomFormData>();

  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [activeEditRoom, setActiveEditRoom] = useState<HealingRoom>();

  const openModal = (roomId: string) => {
    const roomEntity = roomEntities[roomId];
    setActiveEditRoom(roomEntity);
    setEditModalOpen(true);
  };

  const closeModal = () => {
    setActiveEditRoom(undefined);
    setEditModalOpen(false);
    healingRoomForm.resetFields();
  };

  const submitModal = async () => {
    try {
      await healingRoomForm.validateFields();
    } catch (e) {
      const errorAlert = createAlertNotification('Verifique os campos com erro.', AlertNotificationType.Error);
      dispatch(setNotificationAction(errorAlert));
      return;
    }

    const formData: HealingRoomFormData = healingRoomForm.getFieldsValue();

    const roomData: CreateHealingRoomData = {
      ...formData,
      isActive: true,
    };

    try {
      setLoading(true);
      if (activeEditRoom) {
        const updateResult = await dispatch(updateHealingRoomAction({
          id: activeEditRoom.id,
          data: {
            ...roomData,
          },
        }));
        unwrapResult(updateResult);
      } else {
        const createResult = await dispatch(createHealingRoomAction(roomData));
        unwrapResult(createResult);
      }
      const successAlert = createAlertNotification(`
      Sala de cicatrização ${activeEditRoom ? 'atualizada' : 'criada'} com sucesso.
      `, AlertNotificationType.Success);
      dispatch(setNotificationAction(successAlert));
      setLoading(false);
      closeModal();
      pagination.fetch();
    } catch (err) {
      const errorAlert = createAlertNotification('Erro interno ao tentar criar sala de cicatrização. Tente novamente.', AlertNotificationType.Error);
      dispatch(setNotificationAction(errorAlert));
      setLoading(false);
    }
  };

  return (
    <SectionBasePage extra={<Extra onAddClick={() => openModal('')} />}>
      <>
        <Table
          dataSource={rooms}
          pagination={pagination.getInternal()}
          loading={isRemoteLoading}
        >
          <Table.Column
            title="Nome"
            key="name"
            dataIndex="name"
          />
          <Table.Column
            title="Capacidade"
            key="capacity"
            dataIndex="capacity"
          />
          <Table.Column
            title=""
            width="150px"
            dataIndex="id"
            render={(roomId: string) => (
              <ActionButton onClick={() => openModal(roomId)}>
                editar »
              </ActionButton>
            )}
          />
        </Table>
        <EditModal
          formHandler={healingRoomForm}
          onSubmit={submitModal}
          onCancel={closeModal}
          curRoom={activeEditRoom}
          isLoading={isLoading}
          isVisible={isEditModalOpen}
        />
      </>
    </SectionBasePage>
  );
};

export default HealingPage;
